// 게임 저작에서 다루는 "모양" 정의 — 슬롯·스킬·조절값.
//
// ⚠️ **런타임(GAME/)이 진짜 정의를 갖고 있다.** 여기는 그 모양을 빌더 화면에 그리기 위한 사본이다.
//    갈라지면 "빌더에 있는데 게임에 안 나오는 슬롯"이 생긴다 → 아래 대응표를 같이 고칠 것.
//      슬롯 이름      GAME/src/types.ts  GameAssetSlot
//      스킬 4종       GAME/src/games/vertical-shooter/skills.ts
//      조절값 5개     GAME/src/games/vertical-shooter/tuning.ts  PARAM_SPEC
//      표시 크기      GAME/src/games/vertical-shooter/tuning.ts  SPRITE
//      패턴 문자열    GAME/src/types.ts  EnemyPattern (한 줄 5글자)

/** 편대 한 줄의 칸 수. 패턴 문자열의 글자 수와 같다(런타임 FORMATION.lanes). */
export const LANES = 5;

/** 격자 칸을 누를 때 도는 순서. 마지막이 빈칸이라 한 바퀴 돌면 지워진다. */
export const CELL_CYCLE = ["1", "2", "3", "."];

export const PATTERN_DIFFICULTIES = [
	{ value: "easy", label: "쉬움", hint: "초반부터 나온다" },
	{ value: "normal", label: "보통", hint: "중반부터 합류" },
	{ value: "hard", label: "어려움", hint: "후반부터 합류" },
];

/**
 * 플레이 화면에서 그려지는 **표시 크기**(px).
 *
 * 저작 그림의 원본이 몇 px 이든 런타임이 이 크기로 눕힌다(GAME `sizing.ts fitSprite`).
 * 그래서 저작자는 **업로드한 그림만 보고는 게임에서 얼마나 크게 나올지 알 수 없다** —
 * [기본 이미지] 탭의 미리보기가 이 숫자로 실제 크기를 보여주는 이유다.
 *
 * ⚠️ 진짜 정의는 `GAME/src/games/vertical-shooter/tuning.ts` 의 `SPRITE` 다. 여기는 사본이다 —
 *    갈라지면 **미리보기가 거짓말을 한다**(빌더에선 맞게 보이는데 게임에선 다른 크기).
 */
export const PLAY_SPRITE_SIZE = {
	background: { w: 720 },
	coin: { w: 56, h: 56 },
	/** 적 세 종류는 **같은 크기**다 — 구분은 저작자가 그린 색·실루엣이 한다. */
	enemy: { w: 112, h: 112 },
};

/** 적 종류 — 격자 칸 글자('1'|'2'|'3')와 에셋 슬롯을 잇는다. */
export const ENEMY_KINDS = [
	{ cell: "1", slot: "enemy1", label: "약한 적", hint: "체력 1 · 10점", color: "#ff5a5a" },
	{ cell: "2", slot: "enemy2", label: "보통 적", hint: "체력 2 · 20점", color: "#ffa53d" },
	{ cell: "3", slot: "enemy3", label: "단단한 적", hint: "체력 4 · 50점", color: "#b46bff" },
];

/**
 * 이미지 슬롯. `optional: true` 는 비워도 게임이 온전히 도는 자리다
 * (런타임이 내장 기본 그래픽이나 코드 연출로 대신한다).
 */
export const ASSET_GROUPS = [
	{
		title: "배경 · 코인",
		// ⚠️ 플레이어·탄환은 여기 없다 — 캐릭터마다 다르므로 [캐릭터] 탭에서 캐릭터별로 넣는다.
		// ⚠️ 메인 화면 배경·로고도 여기 없다 — [메인 화면] 탭에서 그 화면을 보며 함께 고른다.
		desc: "플레이 중에 보이는 이미지입니다. 비우면 내장 기본 그래픽으로 나옵니다. (플레이어·탄환은 [캐릭터] 탭, 메인 화면 배경·로고는 [메인 화면] 탭에서 지정합니다)",
		slots: [
			{ key: "background", label: "배경", hint: "720×1560 이상, 세로로 이어짐" },
			{ key: "coin", label: "코인", hint: "캐릭터를 사는 재화" },
			// ⚠️ [메인 화면] 탭의 '랭킹보드' 아이콘과 **다른 칸**이다. 랭킹은 '남들과 견준 순위',
			//    여기는 '이번 판 점수'라 뜻이 다르다. 비우면 랭킹 아이콘을 대신 쓰므로,
			//    둘을 같은 그림으로 두고 싶으면 여기를 비워 두면 된다.
			{
				key: "iconScore",
				label: "점수 아이콘",
				hint: "플레이 중 점수 옆. 비우면 랭킹 아이콘을, 그것도 없으면 숫자만 나옵니다",
				optional: true,
			},
		],
	},
	{
		title: "적",
		desc: "적마다 기본 모습과, 맞을 때·죽을 때 모습을 따로 지정할 수 있습니다. 상태 이미지를 비우면 깜빡임·파괴 효과로 대신합니다.",
		enemyStates: true,
		slots: ENEMY_KINDS.flatMap((e) => [
			{ key: e.slot, label: e.label, hint: e.hint, group: e.slot, state: "기본" },
			{ key: `${e.slot}_hit`, label: e.label, group: e.slot, state: "맞을 때", optional: true },
			{ key: `${e.slot}_death`, label: e.label, group: e.slot, state: "죽을 때", optional: true },
		]),
	},
];

/**
 * 기다리는 동안 보이는 두 화면의 배경 — **성격이 다르다**(런타임 GAME/src/ui/screens.ts).
 *
 *   · 부팅 화면  앱을 열 때. 실제로 몇 초 기다리므로 **게이지**가 이 그림 위에서 찬다.
 *   · 진입 화면  시작 → 플레이. "이제 어디로 들어간다"를 보여주는 **연출**(스테이지 라벨+맵 이름).
 *
 * ⚠️ 저장되는 자리는 다른 그림과 **같은 `assets` 맵**이다(슬롯 키만 다르다).
 * ⚠️ 비우면 서로 빌려 쓴다(부팅 → 진입 → 메인 배경). 하나도 없으면 테마 바탕색 —
 *    이 화면들을 안 만든 게임도 깨지지 않는다.
 */
export const BOOT_SLOT = {
	key: "bootBackground",
	label: "부팅 배경",
	hint: "비우면 진입 배경 · 메인 배경을 차례로 빌려 씁니다",
};

/**
 * 진입(시작 → 플레이) 화면 배경. 스테이지 라벨·맵 이름이 이 위에 얹힌다.
 * ⚠️ **맵마다 덮어쓸 수 있다**(MAP_OVERRIDE_GROUPS "진입 화면") — 맵이 자기 그림을 가지면
 *    그 맵으로 들어갈 때 그게 나온다. 여기는 전역 기본값이다.
 */
export const INTRO_SLOT = {
	key: "introBackground",
	label: "진입 배경",
	hint: "맵마다 따로 지정할 수도 있습니다 ([맵] 탭)",
};

/**
 * 기다리는 동안 보이는 화면 둘의 **저작 항목 묶음**.
 *
 * 모양이 똑같다 — 배경 한 칸 + 문구 한 줄 + 토글 하나. 그래서 빌더가 이 배열을 돌며
 * 섹션을 두 개 그린다(MainScreenContext). 항목이 셋이 되어도 화면 코드는 그대로다.
 *
 * ⚠️ `flagKey` 가 가리키는 뜻이 화면마다 다르다 — 부팅은 **게이지**를, 진입은 **스테이지 알약**을
 *    켜고 끈다. 같은 자리에 놓인 토글이지만 같은 것이 아니다.
 */
export const WAIT_SCREENS = [
	{
		slot: BOOT_SLOT,
		title: "부팅 화면",
		desc: "게임을 처음 열어 그림·소리를 받는 동안 보이는 화면입니다. 로딩 게이지가 이 배경 위에서 찹니다.",
		textKey: "bootText",
		textPlaceholder: "불러오는 중",
		textHint: "게이지 위에 한 줄로 나옵니다. 비우면 '불러오는 중'으로 나옵니다.",
		flagKey: "showBootBar",
		flagLabel: "로딩 게이지 보이기",
		flagHint: "끄면 배경과 문구만 나옵니다. 일러스트를 가리고 싶지 않을 때 끄세요.",
	},
	{
		slot: INTRO_SLOT,
		title: "진입 화면",
		desc: "[시작]을 눌러 플레이로 들어가는 동안 보이는 화면입니다. 어느 맵에 들어가는지 보여주는 자리라 게이지가 없습니다(오래 걸릴 때만 진행선이 슬쩍 나옵니다).",
		textKey: "introText",
		textPlaceholder: "(비우면 안 나옵니다)",
		textHint: "맵 이름 아래에 한 줄 더 붙습니다. 예: '준비됐나요?'",
		flagKey: "introShowMap",
		flagLabel: "스테이지 이름 보이기",
		flagHint: "맵 접두어+순번과 맵 이름을 알약으로 보여줍니다. 끄면 배경만 지나갑니다.",
	},
];

/**
 * 메인 화면에만 쓰이는 이미지. [기본 이미지]이 아니라 **[메인 화면] 탭**에 둔다 —
 * 그 화면을 만들면서 함께 고르는 그림이라 문구·버튼과 같은 자리에 있어야 한다.
 *
 * ⚠️ 저장되는 자리는 [기본 이미지]과 **같은 `assets` 맵**이다(슬롯 키만 다르다).
 *    그림은 전부 한 맵에 모아야 서버가 한 번의 쿼리로 URL 을 푼다.
 */
export const MAIN_SCREEN_SLOTS = [
	{
		key: "mainBackground",
		label: "메인 배경",
		hint: "비우면 플레이 배경이 흐르며 대신 깔립니다",
	},
	{ key: "logo", label: "로고", hint: "비우면 타이틀 글자가 대신 나옵니다" },
];

/**
 * 소리 슬롯 — **런타임 `GAME/src/types.ts GameSoundSlot` 와 키가 같아야 한다.**
 * (서버 쪽 사본은 `SERVER/module/util/gameConfig.js SOUND_SLOTS`. 셋이 갈라지면
 *  "빌더에서 올렸는데 게임에서 안 들리는" 소리가 생긴다.)
 *
 * ⚠️ 저장되는 자리는 그림(`assets`)과 **다른 맵**이다(`sound.slots`). 받는 방법이 달라서다 —
 *    그림은 텍스처로, 음원은 디코드해서 받는다. 한 맵에 섞으면 런타임이 mp3 를 이미지로
 *    받으려 든다(migrations/game-page/GAME_sounds.sql 주석).
 * ⚠️ 칸을 늘리지 말 것 — 효과음 자리가 스무 개면 저작자는 음원 스무 개를 구해 와야 한다.
 *    "가장 많이 들리는 순간"만 열어 둔다(PARAM_SPEC·테마 토큰과 같은 생각).
 */
export const SOUND_GROUPS = [
	{
		title: "배경음악",
		// 미리듣기 볼륨을 어느 슬라이더에서 집을지 가른다(배경음악 vs 효과음).
		// ⚠️ 순번(`groups[0]`)으로 판단하지 말 것 — 그룹을 하나 더 넣는 순간 조용히 어긋난다.
		bgm: true,
		desc:
			"계속 흐르는 음악입니다. 길이가 긴 파일은 게임이 시작될 때까지 기다리는 시간을 늘리니 1~2분짜리를 반복(루프)하는 쪽이 낫습니다.",
		slots: [
			{ key: "bgmMain", label: "메인 화면", hint: "게임을 열면 흐릅니다" },
			{
				key: "bgmPlay",
				label: "플레이 중",
				hint: "비우면 메인 음악이 끊기지 않고 그대로 이어집니다",
				optional: true,
			},
		],
	},
	{
		title: "효과음",
		desc:
			"짧고 가벼운 파일을 쓰세요(0.5초 안쪽). 특히 발사음은 초당 여러 번 울립니다 — 긴 소리를 넣으면 서로 겹쳐 뭉개집니다.",
		slots: [
			{ key: "sfxFire", label: "미사일 발사", hint: "가장 자주 울리는 소리입니다", optional: true },
			{ key: "sfxEnemyDown", label: "적 파괴", hint: "", optional: true },
			{
				key: "sfxPlayerHit",
				label: "피격",
				hint: "목숨이 깎일 때. 진동도 함께 울립니다",
				optional: true,
			},
			{ key: "sfxCoin", label: "코인 획득", hint: "", optional: true },
			{ key: "sfxSkill", label: "스킬 사용", hint: "", optional: true },
			{
				key: "sfxGameOver",
				label: "게임 오버",
				hint: "목숨을 다 잃었을 때 (맵 클리어·그만두기에는 울리지 않습니다)",
				optional: true,
			},
		],
	},
];

/**
 * 기본 제공 음원 — 저작자가 파일을 구해 오지 않아도 바로 고를 수 있는 세트.
 *
 * ⚠️ **진짜 정의는 서버 `SERVER/module/util/gameSounds.js` 다.** 여기는 피커에 목록을 그리고
 *    미리듣기를 하기 위한 사본이다 — 갈라지면 "빌더에서 고를 수 있는데 게임에서 안 들리는"
 *    음원이 생긴다(슬롯 목록·테마 토큰과 같은 규약).
 * ⚠️ `ref` 가 **그대로 DB 에 저장된다**(`SOUNDS.slots` 의 값). 저작자가 올린 음원은 assetId 가
 *    들어가는 자리고, 기본 제공은 `builtin:` 접두어로 갈린다. 서버가 그 접두어를 보고 URL 을 푼다.
 * ⚠️ 파일은 `GAME/tools/make-sounds.mjs` 가 합성해 만들고 `deploy-sounds.sh` 가 올린다.
 *    외부 음원을 받아 오지 않는다 — 상용 서비스에 서드파티 바이트를 들이면 라이선스 문서가 따라붙는다.
 */
const BUILTIN_SOUND_DIR =
	"https://groovworks-avatalk-public-file-storage.s3.ap-northeast-2.amazonaws.com/common/game-sounds";

export const BUILTIN_SOUNDS = [
	// ── 효과음 ──
	{ key: "sfx_laser", label: "레이저 발사", hint: "짧게 훑어 내리는 소리 · 0.2초", kind: "sfx" },
	{ key: "sfx_explosion", label: "폭발", hint: "적이 터지는 소리 · 0.5초", kind: "sfx" },
	{ key: "sfx_hit", label: "피격 (둔탁)", hint: "낮게 울리는 소리 · 0.3초", kind: "sfx" },
	{ key: "sfx_coin", label: "코인 획득", hint: "밝은 두 음 · 0.2초", kind: "sfx" },
	{ key: "sfx_powerup", label: "스킬 · 파워업", hint: "올라가는 아르페지오 · 0.3초", kind: "sfx" },
	{ key: "sfx_gameover", label: "게임 오버", hint: "내려가는 세 음 · 0.9초", kind: "sfx" },
	// ── 배경음악 ──
	{ key: "bgm_space", label: "어두운 우주", sub: "슈팅 · SF", hint: "16초 반복 · 마이너", kind: "bgm" },
	{ key: "bgm_cheerful", label: "밝은 동화", sub: "아기자기", hint: "16초 반복 · 메이저", kind: "bgm" },
].map((s) => ({ ...s, ref: `builtin:${s.key}`, url: `${BUILTIN_SOUND_DIR}/${s.key}.mp3` }));

/** 저작 값이 기본 제공을 가리키나(서버 gameSounds.js isBuiltinRef 와 같은 판정). */
export const isBuiltinSoundRef = (v) => typeof v === "string" && v.startsWith("builtin:");

/** 기본 제공 ref → 목록 항목. 모르는 키면 undefined. */
export const builtinSoundOf = (ref) => BUILTIN_SOUNDS.find((s) => s.ref === ref);

/** 평평한 슬롯 목록 — 저장값 정리·미리듣기에서 키만 필요할 때. */
export const SOUND_SLOT_KEYS = SOUND_GROUPS.flatMap((g) => g.slots.map((sl) => sl.key));

/**
 * 볼륨.
 *
 * ⚠️ 기본값이 **배경음악 < 효과음**이다. 음악이 효과음을 덮으면 "쐈다 / 맞았다"가 안 들려
 *    조작 피드백이 죽는다 — 게임에서는 음악보다 그게 먼저다.
 * ⚠️ 기본값의 진짜 정의는 런타임 `GAME/src/config.ts resolveSound` 다. 여기는 **슬라이더가
 *    어디에 서 있을지**를 위한 사본이라, 갈라지면 저장 안 한 상태의 슬라이더가 거짓말을 한다.
 */
export const SOUND_VOLUMES = [
	{ key: "bgmVolume", label: "배경음악 볼륨", min: 0, max: 1, step: 0.05, def: 0.5 },
	{ key: "sfxVolume", label: "효과음 볼륨", min: 0, max: 1, step: 0.05, def: 0.8 },
];

/**
 * 피격 진동.
 *
 * ⚠️ **아이폰에서는 켜도 아무 일이 없다** — iOS 사파리에 Vibration API 자체가 없다.
 *    저작자가 "진동이 안 된다"고 할 자리라 힌트에 적어 둔다.
 */
export const SOUND_VIBRATE = {
	label: "피격 진동",
	hint: "목숨이 깎일 때 기기를 짧게 떨게 합니다. 안드로이드에서만 동작하고, 아이폰(사파리)은 진동 기능 자체가 없어 조용히 무시됩니다. 플레이어가 소리를 끄면 진동도 함께 멈춥니다.",
	def: true,
};

/**
 * UI 색 토큰 — 알약·카드·패널·버튼이 전부 이 일곱 개에서 색을 집는다.
 *
 * ⚠️ **런타임 GAME/src/theme.ts 와 키·기본값이 같아야 한다.** 다르면 빌더에서 고른 색과
 *    게임에 나오는 색이 갈린다.
 * ⚠️ 토큰을 늘리지 말 것 — 요소마다 색을 열면 저작자가 스무 개를 칠해야 하고, 그중 한둘만
 *    어긋나도 화면이 지저분해진다.
 * ⚠️ 플레이 화면(점수·목숨)은 이 색을 안 쓴다. 거기 글자는 패널이 아니라 **배경 그림 위에 바로**
 *    얹혀서, 배경을 모르는 채로는 대비를 보장할 수 없다(런타임 theme.ts 주석 참고).
 */
export const THEME_TOKENS = [
	{ key: "panel", label: "패널 바탕", hint: "알약·카드·팝업의 바탕" },
	{ key: "panelAlt", label: "안쪽 면", hint: "경험치 막대의 빈 부분 등" },
	{ key: "border", label: "테두리", hint: "" },
	{ key: "text", label: "글자", hint: "" },
	{ key: "textMuted", label: "보조 글자", hint: "설명·부제처럼 덜 중요한 글" },
	{ key: "accent", label: "강조", hint: "시작 버튼·선택 표시·경험치 막대" },
	{ key: "accentText", label: "강조 위 글자", hint: "강조색 버튼에 얹히는 글자" },
];

/**
 * 테두리를 긋는 방식. 색이 아니라 **선을 어떻게 긋느냐**라 색 토큰과 따로 둔다.
 *
 * ⚠️ 피그마 시안의 테두리는 피그마 고유의 **브러쉬 스트로크**라(SCATTER 브러쉬) 색·굵기만으로는
 *    옮겨지지 않는다 — 런타임이 경로를 흔들어 그 느낌만 재현한다(GAME/src/ui/sketch.ts).
 */
/**
 * 타이틀·시작 버튼 글자에 외곽선을 두를지. 시안이 그렇게 되어 있다
 * (타이틀=갈색 글자에 흰 테두리, 시작 버튼=흰 글자에 갈색 테두리).
 * ⚠️ 색은 따로 안 받는다 — 이미 있는 토큰에서 집는다(런타임 ui/button.ts, MainScene.drawTopBar).
 */
export const OUTLINE_TEXT = {
	label: "글자 외곽선",
	hint: "배경 그림 위에 얹히는 타이틀·시작 버튼 글자를 테두리로 띄웁니다",
};

export const BORDER_STYLES = [
	{ value: "clean", label: "반듯한 선", hint: "곧고 일정한 두께" },
	{ value: "sketch", label: "손그림 선", hint: "손으로 그은 듯 흔들리는 테두리" },
	{ value: "brushStraight", label: "붓 · 곧은 선", hint: "반듯한 선에 붓 질감 (시안이 이 방식)" },
	{ value: "brush", label: "붓 · 손그림", hint: "손으로 그은 선에 붓 질감" },
];

/** 고르면 토큰이 한꺼번에 채워지는 묶음. 낱개로 칠하기 전에 여기서 시작하면 된다. */
export const THEME_PRESETS = [
	{
		key: "dark",
		label: "어두운 우주",
		sample: "슈팅·SF",
		theme: {
			borderStyle: "clean",
			outlineText: false,
			panel: "#0d1430",
			panelAlt: "#1b2a54",
			border: "#3a4a7a",
			text: "#ffffff",
			textMuted: "#8fa3c8",
			accent: "#4dd2ff",
			accentText: "#062033",
		},
	},
	{
		key: "warm",
		label: "밝은 동화",
		sample: "베이커리·아기자기",
		theme: {
			// 피그마 시안이 브러쉬 테두리라 이 묶음은 손그림이 기본이다.
			borderStyle: "sketch",
			outlineText: true,
			panel: "#fdedd6",
			panelAlt: "#fff3e3",
			border: "#5d402e",
			text: "#423328",
			textMuted: "#8a6f52",
			accent: "#ffd62c",
			accentText: "#5d402e",
		},
	},
];

/** 저작자가 아무것도 안 정했을 때의 색. 런타임 theme.ts DARK_THEME 과 같아야 한다. */
export const DEFAULT_THEME = THEME_PRESETS[0].theme;

/**
 * 고를 수 있는 글꼴 — **봇 빌더 [디자인] 탭과 같은 목록**이다
 * (UI/src/views/PageDesign.svelte fontList). 저작자가 챗봇에서 쓰던 글꼴을 게임에서도
 * 그대로 고를 수 있어야 해서 일부러 맞췄다.
 *
 * ⚠️ `key` 가 곧 CSS `font-family` 이름이다. 서버 util/gameFonts.js 의 파일 표와 **같아야 한다** —
 *    거기 없는 키를 저장하면 셸에 @font-face 가 안 박혀서 글꼴이 조용히 안 먹는다.
 * ⚠️ 글꼴 파일은 이미 같은 S3 버킷(common/builder/fonts/)에 있다. 새로 올릴 것이 없다.
 */
export const GAME_FONTS = [
	{ key: "", label: "기본", sample: "가나다 Ab" },
	{ key: "NotoSansKRBold", label: "노토산스 Bold", sample: "노토산스" },
	{ key: "NanumSquareBold", label: "나눔 스퀘어", sample: "나눔 스퀘어" },
	{ key: "NanumSquareRound", label: "나눔 라운드", sample: "나눔 라운드" },
	{ key: "Katuri", label: "안동 엄마 까투리", sample: "안동엄마까투리" },
	{ key: "Neodgm", label: "neo둥근모", sample: "neo둥근모" },
	{ key: "SeoulHangangExtraBold", label: "서울 한강", sample: "서울한강" },
	{ key: "IMHyemin", label: "IM혜민체", sample: "IM혜민체" },
	{ key: "UhBeeZZIBA", label: "어비찌빠체", sample: "어비찌빠체" },
	// ⚠️ 이 글꼴만 봇 빌더 목록에 없다(게임 전용으로 추가). 봇 빌더 [디자인] 탭에는 안 나온다 —
	//    그쪽 fontList 와 @font-face(webflow CSS)를 같이 손대야 한다.
	{ key: "OwnglyphPDH", label: "온글잎 PDH", sample: "온글잎 손글씨" },
];

/**
 * 메인 화면 상단 스탯바 세 칸 — 켜고 끄는 토글 + 그 칸의 아이콘.
 *
 * ⚠️ 하단 버튼과 달리 **칸이 고정 3개**라 아이콘을 `MAIN.buttons[]` 같은 배열이 아니라
 *    `assets` **슬롯**으로 둔다(슬롯 이름이 안 변한다). 그래서 서버·런타임 로더가
 *    이미 하는 일(배치 URL resolve, BootScene 자동 로드)에 그대로 얹힌다 — 추가 코드가 없다.
 * ⚠️ 재화 칸은 전용 슬롯을 안 만들고 **`coin` 을 그대로 쓴다.** 플레이 중에도 쓰는 그림이라
 *    따로 두면 저작자가 같은 그림을 두 번 올려야 하고, 한쪽만 바꾸면 화면마다 달라진다.
 */
export const STAT_BAR_SLOTS = [
	{
		key: "iconLevel",
		flag: "showLevel",
		label: "레벨 · 경험치",
		hint: "레벨 숫자가 그림 가운데에 얹힙니다 — 가운데가 빈 그림을 쓰세요. 비우면 'Lv 20' 글자로 나옵니다.",
	},
	{
		key: "coin",
		flag: "showCurrency",
		label: "보유 재화",
		hint: "[기본 이미지] 탭의 코인과 같은 그림입니다 — 한쪽을 바꾸면 양쪽이 바뀝니다.",
	},
	{
		key: "iconRanking",
		flag: "showRanking",
		label: "랭킹보드",
		hint: "비우면 '랭킹' 글자만 나옵니다.",
	},
];

/**
 * 하단 버튼이 하는 일.
 *
 * ⚠️ **맵 고르기가 없다** — 맵은 화면 위쪽 캐러셀에서 좌우로 넘겨 고른다.
 * ⚠️ 런타임 types.ts `MainButtonAction` 과 같아야 한다. 여기 없는 값은 서버가 버린다.
 */
export const BUTTON_ACTIONS = [
	{
		value: "characters",
		label: "캐릭터 고르기",
		hint: "캐릭터를 고르고 사는 서랍을 엽니다(스킨 상점)",
	},
	{ value: "guide", label: "안내문 보기", hint: "아래에 쓴 글을 팝업으로 띄웁니다(게임 방법 등)" },
	{ value: "ranking", label: "랭킹보드", hint: "최고 점수 순위를 엽니다" },
	{ value: "link", label: "링크 열기", hint: "새 창으로 주소를 엽니다" },
];

export const newMainButton = () => ({
	id: `b${Math.random().toString(36).substring(2, 10)}`,
	label: "",
	action: "characters",
	iconAssetId: null,
	text: "",
	url: "",
});

/**
 * 맵을 여는 방법. **맵마다 고른다.**
 *
 * ⚠️ 런타임 progress.ts `resolveUnlock` · 서버 util/gameProgress.js 와 같아야 한다.
 * ⚠️ 안 고르면(옛 데이터) "가격이 있으면 코인, 없으면 처음부터"로 읽힌다 — 그래서 기존 게임의
 *    해금이 조용히 바뀌지 않는다.
 */
export const MAP_UNLOCK_TYPES = [
	{ value: "always", label: "처음부터", hint: "잠그지 않습니다" },
	{ value: "coin", label: "코인 구매", hint: "아래 가격만큼의 코인을 치릅니다" },
	{ value: "clear", label: "앞 맵 클리어", hint: "고른 맵을 깨야 열립니다(안 고르면 바로 앞 맵)" },
	{ value: "score", label: "누적 점수", hint: "여태 쌓은 점수가 기준을 넘으면 열립니다" },
	{ value: "level", label: "레벨 도달", hint: "레벨은 [난이도] 탭의 성장 곡선으로 오릅니다" },
];

/**
 * 성장 곡선 — 레벨과 경험치.
 *
 * ⚠️ 경험치는 **누적 점수에서 파생**된다(따로 쌓지 않는다). 그래서 곡선을 바꾸면 이미 논 사람의
 *    레벨도 같이 움직인다 — 공개한 뒤에 크게 바꾸면 "레벨이 내려갔다"가 된다.
 * ⚠️ min/max 는 런타임 progress.ts normalizeProgression 과 같아야 한다.
 */
export const PROGRESSION_PARAMS = [
	{ key: "expPerScore", label: "점수당 경험치", min: 0.01, max: 100, step: 0.01, def: 1, unit: "배",
	  hint: "점수 1점이 경험치 몇이 되는지" },
	{ key: "baseExp", label: "첫 레벨업 경험치", min: 1, max: 1000000, step: 100, def: 1000, unit: "",
	  hint: "1→2 레벨에 필요한 양" },
	{ key: "growth", label: "레벨마다 증가율", min: 1, max: 3, step: 0.01, def: 1.12, unit: "배",
	  hint: "1이면 모든 레벨이 같은 양" },
	{ key: "maxLevel", label: "최고 레벨", min: 1, max: 999, step: 1, def: 99, unit: "", hint: "" },
];

/**
 * 스킬. `effectHint` 는 이펙트 슬롯을 비웠을 때 무엇으로 대신하는지 — 저작자가
 * "안 올리면 어떻게 되나"를 화면에서 바로 알 수 있어야 한다.
 */
/**
 * 내장 **효과** — 코드가 가진 동작이라 저작자가 늘릴 수 없다.
 * (런타임 GAME/src/types.ts EffectId · tuning.ts EFFECT 와 같은 목록이어야 한다.)
 *
 * 저작자가 만드는 건 효과가 아니라 **스킬**이다 — 효과 하나를 고르고 거기에 이름·그림을 입힌다.
 * 그래서 "소금폭탄"과 "작은 폭탄"처럼 효과는 같고 생김새만 다른 둘을 만들 수 있다.
 */
export const EFFECTS = [
	{
		value: "bomb",
		label: "폭탄",
		desc: "화면에 있는 적을 한 번에 없앱니다",
		effectHint: "비우면 내장 폭발 효과로 나옵니다",
		color: "#ff5a5a",
		instant: true,
	},
	{
		value: "barrier",
		label: "무적",
		desc: "잠시 부딪혀도 목숨이 줄지 않습니다",
		effectHint: "비우면 내장 보호막 링으로 나옵니다",
		color: "#4dd2ff",
	},
	{
		value: "slow",
		label: "적 감속",
		desc: "잠시 적이 느리게 내려옵니다",
		effectHint: "쓰지 않습니다(화면에 글자로만 뜹니다)",
		color: "#a78bfa",
		power: {
			label: "남는 속도",
			min: 0.15,
			max: 0.9,
			step: 0.05,
			def: 0.35,
			hint: "0.35 = 평소의 35% 속도로 기어옵니다. 작을수록 많이 느려집니다",
		},
	},
	{
		value: "scoreBoost",
		label: "포인트 배수",
		desc: "잠시 적을 잡은 점수가 배로 들어옵니다",
		effectHint: "쓰지 않습니다(화면에 글자로만 뜹니다)",
		color: "#ffe66d",
		power: { label: "점수 배수", min: 1.5, max: 5, step: 0.5, def: 2, hint: "생존 점수에는 걸리지 않습니다" },
	},
	{
		value: "multishot",
		label: "멀티샷",
		desc: "잠시 탄이 3발 부채꼴로 나갑니다",
		effectHint: "비우면 그 캐릭터의 탄환 그림을 그대로 씁니다",
		color: "#5ce1a6",
	},
	{
		value: "giantshot",
		label: "거대탄",
		desc: "잠시 크고 적을 관통하는 탄이 나갑니다",
		effectHint: "비우면 그 캐릭터의 탄환 그림을 그대로 씁니다",
		color: "#ff9f43",
	},
];

export const effectOf = (value) => EFFECTS.find((e) => e.value === value) || EFFECTS[0];

/**
 * 저작자가 스킬을 하나도 안 만들었을 때 런타임이 쓰는 내장 세트.
 * ⚠️ **id 가 효과 이름 그대로**다 — 런타임 `skills.ts BUILTIN_SKILLS` 와 같아야
 *    캐릭터의 `skill: "barrier"` 가 그대로 유효한 참조가 된다.
 */
export const BUILTIN_SKILL_CHOICES = EFFECTS.map((e) => ({ id: e.value, name: e.label }));

/**
 * 지속시간 범위. ⚠️ 런타임 `tuning.ts SKILL_LIMITS.durationMs` 와 같아야 한다 —
 * 여기가 더 넓으면 저장은 되는데 게임이 조용히 값을 깎아, 저작자는 "안 먹는다"고 느낀다.
 */
export const SKILL_DURATION = { min: 500, max: 15000, step: 500, def: 3000 };

/**
 * 떨어지는 **비중** — 후보 중 무엇이 나올지를 가른다.
 * ⚠️ "얼마나 자주 떨어지나"(맵/난이도의 드롭 확률)와 **다른 축**이다. 둘을 한 숫자로 합치면
 *    "폭탄을 자주 주고 싶다"가 "아이템이 자주 떨어진다"가 돼 버린다.
 * ⚠️ 런타임 `tuning.ts SKILL_LIMITS.weight` 와 범위가 같아야 한다.
 */
export const SKILL_WEIGHT = { min: 1, max: 10, step: 1, def: 1 };

/**
 * 플레이 화면 아이템 칸 수. ⚠️ 런타임 `constants.ts SLOT_COUNT` 와 같아야 한다.
 *
 * 칸마다 스킬이 **미리 배정**되어 아이콘이 늘 보인다. 그래서 떨어지는 스킬도 이 수만큼이다 —
 * 담을 칸이 없는 아이템이 떨어지면 주워도 사라져서 피할 수 없는 손해로 읽힌다.
 */
export const SLOT_COUNT = 2;

/** 스킬 그림 3칸. 전부 스킬 객체 **안에** 들어간다(슬롯 맵이 아니다 — 지우면 같이 사라져야 한다). */
export const SKILL_SLOT_KINDS = [
	{ field: "iconAssetId", label: "아이콘", hint: "화면 아래 버튼에 나옵니다" },
	{ field: "dropAssetId", label: "드롭", hint: "적이 떨어뜨리는 모습. 비우면 아이콘을 씁니다" },
	{ field: "effectAssetId", label: "이펙트", hint: "" },
];

export const newSkill = () => ({
	id: `s${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	effect: "bomb",
	// 비워두면 런타임이 효과 기본값으로 논다 — 저작자가 만진 것만 숫자가 생긴다.
	durationMs: null,
	power: null,
	weight: null,
	iconAssetId: null,
	dropAssetId: null,
	effectAssetId: null,
});
/**
 * 저작자 조절값. ⚠️ min/max/def 는 런타임 PARAM_SPEC 과 같아야 한다 —
 * 여기가 더 넓으면 저장은 되는데 게임이 조용히 값을 깎아, 저작자는 "슬라이더가 안 먹는다"고 느낀다.
 */
export const PARAMS = [
	{ key: "paceScale", label: "게임 속도", min: 0.5, max: 2.5, step: 0.1, def: 1, unit: "배",
	  hint: "적이 내려오는 속도와 배경이 흐르는 속도" },
	{ key: "gapScale", label: "편대 간격", min: 0.3, max: 2.5, step: 0.1, def: 1, unit: "배",
	  hint: "작을수록 쉴 틈 없이 몰아칩니다" },
	{ key: "lives", label: "목숨 수", min: 1, max: 9, step: 1, def: 3, unit: "개",
	  hint: "1로 두면 한 번만 부딪혀도 끝납니다" },
	{ key: "fireRateScale", label: "연사 속도", min: 0.5, max: 2.5, step: 0.1, def: 1, unit: "배",
	  hint: "클수록 탄이 촘촘하게 나갑니다" },
	{ key: "dropChance", label: "스킬 드롭 확률", min: 0, max: 0.5, step: 0.01, def: 0.09, unit: "",
	  hint: "적을 잡았을 때 스킬이 떨어질 확률" },
	{ key: "coinRate", label: "코인 획득량", min: 0.2, max: 4, step: 0.1, def: 1, unit: "배",
	  hint: "캐릭터를 얼마나 빨리 살 수 있을지 정합니다" },
];

/**
 * 캐릭터별 능력치. **절대값이 아니라 [난이도] 탭 값 위에 얹히는 보정**이다
 * (런타임 characters.ts normalizeStats 와 범위가 같아야 한다).
 */
export const CHARACTER_STATS = [
	{ key: "livesDelta", label: "목숨", min: -2, max: 5, step: 1, def: 0, kind: "delta",
	  hint: "기본 목숨에서 더하거나 뺍니다" },
	{ key: "fireRateScale", label: "연사 속도", min: 0.4, max: 3, step: 0.1, def: 1, kind: "scale",
	  hint: "클수록 탄이 촘촘하게 나갑니다" },
	{ key: "moveSpeedScale", label: "이동 속도", min: 0.4, max: 2, step: 0.1, def: 1, kind: "scale",
	  hint: "손가락을 얼마나 민첩하게 따라오는지" },
	{ key: "coinScale", label: "코인 획득량", min: 0.2, max: 5, step: 0.1, def: 1, kind: "scale",
	  hint: "이 캐릭터로 놀 때 코인이 더 모입니다" },
];

/** 캐릭터가 쓰는 이미지 2칸. 비우면 내장 기본 그림으로 나온다. */
export const CHARACTER_SLOT_KINDS = [
	{ field: "playerAssetId", label: "플레이어", hint: "투명 PNG 권장" },
	{ field: "bulletAssetId", label: "탄환", hint: "투명 PNG 권장" },
	/**
	 * 플레이 화면 전용 기체. 비우면 위의 '플레이어' 그림을 그대로 쓴다.
	 *
	 * 왜 칸이 둘인가: 메인 화면 캐릭터는 **보여주는 그림**(크고 정면, 장식 많음)이고,
	 * 플레이 화면은 작게 위를 향해 날아가는 **조종하는 물체**다. 한 장으로 둘 다 하면
	 * 둘 중 하나가 어색해진다.
	 */
	{ field: "ingameAssetId", label: "인게임", hint: "비우면 '플레이어' 그림을 씁니다" },
];

/**
 * 맵 난이도 보정. **절대값이 아니라 전역 [난이도] 탭 값에 곱해지는 배율**이다
 * (런타임 maps.ts normalizeMapParams 와 범위가 같아야 한다).
 */
export const MAP_PARAMS = [
	{ key: "paceScale", label: "속도", min: 0.4, max: 3, step: 0.1, def: 1, kind: "scale",
	  hint: "어려운 맵일수록 크게 — 적이 더 빨리 내려옵니다" },
	{ key: "gapScale", label: "편대 간격", min: 0.3, max: 3, step: 0.1, def: 1, kind: "scale",
	  hint: "작을수록 쉴 틈 없이 몰아칩니다" },
	{ key: "livesDelta", label: "목숨", min: -2, max: 5, step: 1, def: 0, kind: "delta",
	  hint: "이 맵에서만 목숨을 더하거나 뺍니다" },
	{ key: "coinScale", label: "코인", min: 0.2, max: 5, step: 0.1, def: 1, kind: "scale",
	  hint: "어려운 맵에 더 주면 도전할 이유가 생깁니다" },
	{ key: "dropScale", label: "스킬 드롭 확률", min: 0, max: 3, step: 0.1, def: 1, kind: "scale",
	  hint: "[난이도] 탭 확률에 곱해집니다. 0으로 두면 이 맵엔 아이템이 안 떨어집니다" },
];

/**
 * 맵이 덮어쓸 수 있는 이미지. 안 넣으면 [기본 이미지] 탭의 전역 그림이 나온다.
 *
 * ⚠️ 적은 **상태 세트(기본/맞을 때/죽을 때)를 통째로** 덮을 수 있어야 한다. 기본만 바꾸면
 *    바다 맵인데 피격·사망만 우주 적 그림이 나오는 식으로 어긋난다.
 * ⚠️ 플레이어·탄환은 여기 없다 — 맵이 아니라 **캐릭터**의 것이다.
 */
export const MAP_OVERRIDE_GROUPS = [
	{
		title: "화면",
		slots: [
			{ key: "background", label: "배경" },
			{ key: "coin", label: "코인" },
			// 이 맵으로 들어갈 때 보이는 진입 화면 배경. 비우면 [메인 화면] 탭의 전역 진입 배경.
			{ key: "introBackground", label: "진입 화면" },
		],
	},
	...ENEMY_KINDS.map((e) => ({
		title: e.label,
		color: e.color,
		slots: [
			{ key: e.slot, label: "기본" },
			{ key: `${e.slot}_hit`, label: "맞을 때" },
			{ key: `${e.slot}_death`, label: "죽을 때" },
		],
	})),
];

export const MAP_MODES = [
	{ value: "endless", label: "무한", hint: "죽을 때까지 계속됩니다" },
	{ value: "goal", label: "목표 달성", hint: "목표를 넘기면 클리어" },
];

export const MAP_GOAL_TYPES = [
	{ value: "score", label: "점수", unit: "점", def: 30000 },
	{ value: "time", label: "생존 시간", unit: "초", def: 90 },
];

/* ══════════════════════ 스테이지 · 보스 ══════════════════════
 * 맵 ⊃ 스테이지 ⊃ 편대. 상단 배지가 스테이지 번호를 보여주므로, 플레이어에게 이건
 * "얼마나 왔나"다 — 스테이지를 안 만들면 맵이 통째로 한 판이 된다(예전 동작).
 */

export const STAGE_ADVANCE_TYPES = [
	{ value: "time", label: "시간", unit: "초", def: 60, min: 10, max: 600,
	  hint: "이만큼 버티면 다음 단계로" },
	{ value: "score", label: "점수", unit: "점", def: 5000, min: 500, max: 500000,
	  hint: "이 단계에서 이만큼 벌면 다음 단계로" },
];

export const advanceTypeOf = (v) => STAGE_ADVANCE_TYPES.find((t) => t.value === v) || STAGE_ADVANCE_TYPES[0];

/**
 * 스테이지 난이도 보정 — **맵 보정 위에 또 곱해진다**(난이도의 네 번째 층).
 * ⚠️ 목숨은 없다. 판 도중에 목숨이 늘고 주는 건 말이 안 되고, 런타임도 스테이지의 목숨 값을
 *    안 읽는다 — 칸만 만들면 "만졌는데 아무 일도 없다"가 된다. 목숨은 [맵] 에서만.
 */
export const STAGE_PARAMS = MAP_PARAMS.filter((p) => p.key !== "livesDelta");

export const newStage = (index = 0) => ({
	id: `st${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	// 뒤 단계일수록 조금씩 빡세게 — 저작자가 아무것도 안 만져도 '올라간다'는 느낌이 나게.
	advance: { type: "time", value: 60 },
	patternIds: [],
	params: index === 0 ? {} : { paceScale: Math.min(3, 1 + index * 0.25) },
	boss: null,
});

/** 보스가 움직이는 방식. 제자리에 서 있으면 맞히기만 하는 과녁이 된다. */
export const BOSS_MOVES = [
	{ value: "sweep", label: "좌우로", hint: "가장 단순하고 읽기 쉽습니다" },
	{ value: "figure8", label: "8자로", hint: "위아래로도 움직여 겨냥이 어렵습니다" },
	{ value: "dive", label: "이따금 돌진", hint: "가까워질 때가 때릴 기회이자 위험입니다" },
];

/** 탄막 모양. 세 가지면 충분하고, 변주는 개수·주기·속도로 한다. */
export const VOLLEY_PATTERNS = [
	{ value: "spread", label: "부채꼴", hint: "아래로 퍼집니다" },
	{ value: "aimed", label: "조준", hint: "플레이어를 노립니다 — 움직여야 피합니다" },
	{ value: "ring", label: "원형", hint: "사방으로 퍼집니다" },
];

/** ⚠️ min/max 는 런타임이 쓰는 값과 어긋나면 "슬라이더가 안 먹는다"가 된다. */
export const BOSS_STATS = [
	{ key: "hp", label: "체력", min: 10, max: 2000, step: 10, def: 200,
	  hint: "탄 한 발에 1 씩 깎입니다" },
	{ key: "score", label: "처치 점수", min: 0, max: 100000, step: 100, def: 1000 },
	{ key: "coins", label: "처치 코인", min: 0, max: 500, step: 5, def: 30,
	  hint: "보스를 잡을 이유를 만드는 자리입니다" },
	{ key: "timeLimitSec", label: "제한 시간", min: 10, max: 180, step: 5, def: 45,
	  hint: "⚠️ 이 안에 못 잡으면 보스가 물러납니다(보상 없음). 0으로 둘 수 없습니다" },
];

export const VOLLEY_SPEC = [
	{ key: "count", label: "탄 수", min: 1, max: 24, step: 1, def: 5 },
	{ key: "intervalMs", label: "발사 간격", min: 200, max: 4000, step: 100, def: 1200, unit: "ms" },
	{ key: "speed", label: "탄속", min: 80, max: 700, step: 20, def: 260 },
];

/** 보스 그림 3칸. 캐릭터·스킬과 같은 이유로 **스테이지 객체 안**에 들어간다. */
export const BOSS_SLOT_KINDS = [
	{ field: "assetId", label: "기본", hint: "투명 PNG 권장" },
	{ field: "hitAssetId", label: "맞을 때", hint: "비우면 흰색 번쩍임", optional: true },
	{ field: "deathAssetId", label: "죽을 때", hint: "비우면 그대로 사라집니다", optional: true },
];

export const newVolley = () => ({ pattern: "spread", count: 5, intervalMs: 1200, speed: 260 });

export const newBoss = () => ({
	name: "",
	hp: 200,
	score: 1000,
	coins: 30,
	move: "sweep",
	timeLimitSec: 45,
	volleys: [newVolley()],
	assetId: null,
	hitAssetId: null,
	deathAssetId: null,
});

export const newMap = (index = 0) => ({
	id: `m${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	// 코인 구매로 바꿨을 때 쓸 가격. 첫 맵은 0원 — 아무도 게임을 시작 못 하는 상태가 되지 않게.
	price: index === 0 ? 0 : 200,
	// 첫 맵은 항상 열려 있어야 한다. 그 뒤는 '앞 맵 클리어'로 시작한다 —
	// 스테이지를 여러 개 만드는 이유가 대개 순서대로 밟게 하려는 것이라서.
	unlock: index === 0 ? { type: "always" } : { type: "clear" },
	mode: "endless",
	goal: null,
	params: {},
	patternIds: [],
	// 비우면 전역 드롭 후보 전부를 쓴다(편대 patternIds 와 같은 규약).
	skillIds: [],
	assets: {},
	// 비우면 맵이 통째로 한 판(예전 동작). 만들면 맵 안에서 단계가 올라간다.
	stages: [],
});

export const newCharacter = (index = 0) => ({
	id: `c${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	// 첫 캐릭터는 0원 — 아무도 못 고르는 게임이 되지 않게.
	price: index === 0 ? 0 : 100,
	skill: "barrier",
	stats: {},
	playerAssetId: null,
	bulletAssetId: null,
	ingameAssetId: null,
});

/** 패턴 한 줄을 격자 칸 배열로. 글자가 모자라거나 남아도 레인 수에 맞춘다(런타임 parseRow와 같은 규칙). */
export const rowToCells = (row) => {
	const cells = [];
	for (let i = 0; i < LANES; i++) {
		const c = (row || "")[i];
		cells.push(CELL_CYCLE.includes(c) && c !== "." ? c : ".");
	}
	return cells;
};

export const cellsToRow = (cells) => cells.join("");

/** 새 패턴의 기본 모양 — 빈 줄 하나로 시작한다(저작자가 채워 넣게). */
export const emptyRow = () => ".".repeat(LANES);

export const newPattern = () => ({
	id: `p${Math.random().toString(36).substring(2, 10)}`,
	name: "",
	difficulty: "normal",
	rows: [emptyRow()],
});

/* ══════════════════════ 세트 일괄 생성 (AI) ══════════════════════
 * 슬롯을 하나씩 뽑으면 **화풍이 제각각**이 된다. 그래서 한 번에 순서대로 돌리되,
 * 적의 상태 이미지(피격·사망)는 **그 적의 기본 이미지를 참조로 파생**시킨다(image-to-image).
 * 같은 적으로 보여야 하는데, 따로 생성하면 절대 같은 적이 안 나온다.
 *
 * ⚠️ 순서가 규칙이다 — `refFrom` 은 **앞에서 이미 만든 슬롯**만 가리킬 수 있다.
 * ⚠️ 로고는 넣지 않는다. 브랜드 성격이라 세트 화풍에 섞을 물건이 아니고, 한 번 만들면 끝이다.
 */
export const ASSET_SET_STEPS = [
	{ slot: "background", label: "배경", imageType: "background",
	  prompt: "세로로 끝없이 이어지는 게임 배경. 위아래가 자연스럽게 이어지도록 위쪽과 아래쪽 가장자리를 비슷한 톤으로." },
	{ slot: "coin", label: "코인", imageType: "sprite",
	  prompt: "게임에서 주워 모으는 재화 코인 한 개. 작게 줄여도 알아볼 수 있게." },

	...ENEMY_KINDS.flatMap((e, i) => {
		const rank = ["가장 약하고 작은", "중간 크기의", "가장 크고 단단한"][i];
		return [
			{ slot: e.slot, label: `${e.label} 기본`, imageType: "sprite",
			  prompt: `${rank} 적 비행체. 아래를 향한다.` },
			{ slot: `${e.slot}_hit`, label: `${e.label} 피격`, imageType: "sprite", refFrom: e.slot,
			  prompt: "참조 이미지와 **완전히 같은 적**이 총에 맞아 번쩍이는 순간. 형태·색·디자인은 그대로 두고 밝게 번쩍이는 느낌만 더한다." },
			{ slot: `${e.slot}_death`, label: `${e.label} 파괴`, imageType: "sprite", refFrom: e.slot,
			  prompt: "참조 이미지와 **같은 적**이 파괴되어 부서지는 순간. 조각나고 불꽃이 이는 모습." },
		];
	}),
];

/** 캐릭터 한 명의 세트(플레이어·탄환). 캐릭터마다 따로 돌린다. */
export const CHARACTER_SET_STEPS = [
	{ slot: "playerAssetId", label: "플레이어", imageType: "sprite",
	  prompt: "플레이어가 조종하는 비행체. 위를 향한다." },
	{ slot: "bulletAssetId", label: "탄환", imageType: "sprite", refFrom: "playerAssetId",
	  prompt: "참조 이미지의 비행체가 쏘는 **탄환 한 발**. 비행체와 같은 색감. 위를 향해 날아가는 작고 길쭉한 형태." },
];
